import React, { useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { FaTrash } from "react-icons/fa";
import { WordBankItem } from "src/features/ai-sessions/types";
import { useDeleteWordBankItemMutation } from "../word-bank.service";

type Props = {
  data: WordBankItem[];
};

const statusBadgeClass = (status?: WordBankItem["status"]) => {
  switch (status) {
    case "WEAK":
      return "bg-rose-100 text-rose-700 border-rose-200";
    case "GETTING_IT":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "MASTERED":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const grammarBadgeClass = (grammarLabel?: WordBankItem["grammarLabel"]) => {
  if (!grammarLabel) return "bg-gray-100 text-gray-700 border-gray-200";
  if (grammarLabel.includes("VERB")) return "bg-purple-100 text-purple-700 border-purple-200";
  if (grammarLabel.includes("NOUN")) return "bg-blue-100 text-blue-700 border-blue-200";
  if (grammarLabel.includes("ADJECTIVE")) return "bg-indigo-100 text-indigo-700 border-indigo-200";
  return "bg-cyan-100 text-cyan-700 border-cyan-200";
};

const formatDateTime = (value?: { toDate?: () => Date } | Date | null) => {
  if (!value) return { date: "N/A", time: "" };

  const date = value instanceof Date ? value : value.toDate?.();
  if (!date) return { date: "N/A", time: "" };

  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  };
};

const formatCellValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "N/A";
  return String(value);
};

export const WordBankTable: React.FC<Props> = ({ data }) => {
  const [deleteWordBankItem, { isLoading: isDeleting }] = useDeleteWordBankItemMutation();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (wordBankItem: WordBankItem) => {
    const result = await Swal.fire({
      title: "Delete word?",
      text: `This will permanently delete "${wordBankItem.word}" from the word bank.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setDeletingId(wordBankItem.id);

    try {
      await deleteWordBankItem(wordBankItem.id).unwrap();

      await Swal.fire({
        title: "Deleted",
        text: "The word bank item was deleted successfully.",
        icon: "success",
        confirmButtonColor: "#10b981",
      });
    } catch (error: any) {
      const message = String(
        error?.data || error?.error || error?.message || "Failed to delete the word bank item."
      );

      await Swal.fire({
        title: "Delete failed",
        text: message,
        icon: "error",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[1820px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gray-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">ID</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">User</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Word</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Translation</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Grammar</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Status</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Language</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Seen</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Source</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Last Practiced</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Created</th>
            <th className="px-2 py-2 sm:px-4 sm:py-3 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={12} className="text-center py-4 text-xl text-gray-500">
                No Word Bank Items
              </td>
            </tr>
          )}

          {data.map((wordBankItem) => {
            const lastPracticedAt = formatDateTime(wordBankItem.lastPracticedAt);
            const createdAt = formatDateTime(wordBankItem.createdAt);

            return (
              <tr key={wordBankItem.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200 font-mono text-[11px] sm:text-xs" title={wordBankItem.id}>
                  {wordBankItem.id}
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  {wordBankItem.userId ? (
                    <Link
                      to={`/users/${wordBankItem.userId}`}
                      className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 text-white border border-sky-300 shadow-sm hover:shadow-md hover:from-cyan-600 hover:via-sky-600 hover:to-indigo-600 transition-all duration-200 transform hover:scale-110 will-change-transform no-underline font-mono font-semibold text-[10px] sm:text-[11px] focus:outline-none focus:ring-2 focus:ring-sky-300 focus:ring-offset-1 select-text"
                    >
                      {wordBankItem.userId}
                    </Link>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="font-semibold text-slate-800">{formatCellValue(wordBankItem.word)}</div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="text-slate-700">{formatCellValue(wordBankItem.translation)}</div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-semibold ${grammarBadgeClass(wordBankItem.grammarLabel)}`}>
                    {formatCellValue(wordBankItem.grammarLabel)}
                  </span>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-semibold ${statusBadgeClass(wordBankItem.status)}`}>
                    {formatCellValue(wordBankItem.status)}
                  </span>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <span className="inline-flex items-center px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-semibold bg-slate-100 text-slate-700 border-slate-200">
                    {formatCellValue(wordBankItem.langCode).toUpperCase()}
                  </span>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <span className="font-semibold text-amber-700">{formatCellValue(wordBankItem.seenCount)}</span>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <span className="inline-flex items-center px-2 py-1 rounded-full border text-[10px] sm:text-[11px] font-semibold bg-cyan-50 text-cyan-700 border-cyan-200">
                    {formatCellValue(wordBankItem.source?.from)}
                  </span>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{lastPracticedAt.date}</span>
                    <span className="text-[9px] sm:text-[10px] text-gray-500">{lastPracticedAt.time}</span>
                  </div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] sm:text-[11px] font-semibold text-gray-700">{createdAt.date}</span>
                    <span className="text-[9px] sm:text-[10px] text-gray-500">{createdAt.time}</span>
                  </div>
                </td>
                <td className="px-2 py-2 sm:px-4 sm:py-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => handleDelete(wordBankItem)}
                    disabled={isDeleting && deletingId === wordBankItem.id}
                    className="inline-flex items-center justify-center px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 hover:border-red-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={`Delete ${wordBankItem.word}`}
                  >
                    <FaTrash className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default WordBankTable;
